import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { navigate } from "../router";
import { getTelegram, haptic } from "../telegram";
import { useGameSocket } from "../useGameSocket";

const SEAT_EMOJI = ["🔴", "🔵", "🟢", "🟡"];

export function LobbyPage({ roomCode }: { roomCode: string }) {
  const { status, state, send, lastError } = useGameSocket(roomCode);
  const [error, setError] = useState<string | null>(null);
  const [myId, setMyId] = useState<number | null>(null);

  useEffect(() => {
    api
      .auth()
      .then((r) => setMyId(r.user.telegramId))
      .catch(() => {});
  }, []);

  // join via HTTP once (idempotent-ish), then rely on socket state
  useEffect(() => {
    if (!roomCode) return;
    api.joinRoom(roomCode).catch((e: Error) => {
      if (!/already/i.test(e.message)) setError(e.message);
    });
  }, [roomCode]);

  useEffect(() => {
    if (state?.roomStatus === "PLAYING") {
      navigate("game", { room: roomCode });
    }
  }, [state?.roomStatus, roomCode]);

  const isHost = useMemo(
    () => state?.players.some((p) => p.userId === myId && p.isHost) ?? false,
    [state, myId],
  );
  const canStart = isHost && (state?.players.length ?? 0) >= (state?.config.minPlayers ?? 2);

  const start = () => {
    haptic("medium");
    send({ type: "START_GAME" });
  };

  const share = async () => {
    let link = `${window.location.origin}${window.location.pathname}#/lobby?room=${roomCode}`;
    try {
      const cfg = await api.config();
      const code = roomCode.replace(/-/g, "");
      if (cfg.botUsername) {
        link = cfg.appShortName
          ? `https://t.me/${cfg.botUsername}/${cfg.appShortName}?startapp=${code}`
          : `https://t.me/${cfg.botUsername}?start=${code}`;
      }
    } catch {
      // fall back to raw link
    }
    const tg = getTelegram();
    const shareUrl =
      "https://t.me/share/url?url=" +
      encodeURIComponent(link) +
      "&text=" +
      encodeURIComponent(`بیایید منچ! کد اتاق: ${roomCode}`);
    if (tg?.openTelegramLink) tg.openTelegramLink(shareUrl);
    else window.open(shareUrl, "_blank");
  };

  if (error) {
    return (
      <div className="screen center">
        <div className="card">
          <h2>⚠️ {error}</h2>
          <button className="btn block" onClick={() => navigate("home")}>
            🏠 بازگشت
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <header className="header">
        <h1>لابی</h1>
        <ConnectionBadge status={status} />
      </header>

      <div className="card center">
        <div className="label">کد اتاق</div>
        <div className="room-code" dir="ltr">
          {roomCode.replace(/-/g, "").replace(/(.{4})/, "$1-")}
        </div>
      </div>

      <div className="card">
        {Array.from({ length: state?.config.maxPlayers ?? 4 }).map((_, i) => {
          const p = state?.players[i];
          return (
            <div key={i} className="player-row">
              <span className="seat">{SEAT_EMOJI[i]}</span>
              <span>
                {p ? p.firstName ?? p.username ?? `بازیکن ${i + 1}` : "در انتظار بازیکن…"}
                {p?.isHost && " 👑"}
                {p && !p.isConnected && " (آفلاین)"}
              </span>
            </div>
          );
        })}
      </div>

      <div className="card">
        <button className="btn primary block" onClick={start} disabled={!canStart}>
          🎮 شروع بازی
        </button>
        {isHost && !canStart && (
          <p className="muted center">حداقل {state?.config.minPlayers ?? 2} بازیکن لازم است</p>
        )}
        {!isHost && <p className="muted center">فقط میزبان می‌تواند بازی را شروع کند</p>}
        <button className="btn block" onClick={share}>
          📤 دعوت دوستان
        </button>
      </div>

      {lastError && <div className="toast error">{lastError.message}</div>}
    </div>
  );
}

export function ConnectionBadge({ status }: { status: string }) {
  if (status === "connected") return <span className="badge ok">🟢 متصل</span>;
  if (status === "reconnecting") return <span className="badge warn">🔴 اتصال قطع شد — در حال تلاش مجدد…</span>;
  return <span className="badge">… در حال اتصال</span>;
}
